/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../api.js";
import _ from "underscore";
import googleMaps from '../util/google-maps.js';
import mapStyles from '../map/styles.js';
import DensityMapType from '../map/density-map-type.js';
import markerLayer from '../map/remote-marker-layer.js';

var SpeciesMapViewModel = function(params) {
	var self = this;

	// Set default params
	params = _.defaults(params, {
	});

	// Share species filter globally
	self.speciesFilter = window.speciesFilter || ko.observable(null);
	window.speciesFilter = self.speciesFilter;

	self.species = ko.observable();
	self.loaded = ko.observable(false);

	self.map = ko.observable();
	self.mapOptions = ko.observable();

	self.filterCriteria = ko.pureComputed(function() {
		var criteria = {};
		if(self.speciesFilter()) {
			criteria.species_id = self.speciesFilter().id;
		}
		return criteria;
	});

	Promise.all([birdata.speciesList()]).then(function([species]) {
		self.species(species);
		self.loaded(true);
	});

	googleMaps.load().then(function() {
		var center = new google.maps.LatLng(-22.50831, 136.078612);
		var zoom = 4;

		self.mapOptions(googleMaps.defaultOptions({
			center: center,
			zoom: zoom
		}));
	});

	self.visibleTiles = ko.observable(0);
	self.loadingTiles = ko.observable(0);
	self.mapTilesLoaded = ko.pureComputed(function() {
		// console.log(self.loadingTiles());
		return self.loadingTiles() == 0;
	});

	function setupMap(map) {
		if(setupMap.called) return;
		setupMap.called = true;

		var overlay = new DensityMapType({
			maxZoom: 11,
			tileSize: new google.maps.Size(256, 256),
			map: map,
			criteriaFn: self.filterCriteria,
			visibleTiles: self.visibleTiles,
			loadingTiles: self.loadingTiles
		});

		map.overlayMapTypes.insertAt(0, overlay);

		// Set up survey points
		var surveyPointLayer = markerLayer(map, {
			minZoom: 12,
			visibleTilesObs: self.visibleTiles,
			loadingTilesObs: self.loadingTiles,
			pointsForTile: function(tile) {
				var projection = map.getProjection();
				var scale = 256 / Math.pow(2, tile.z);
				var sw = projection.fromPointToLatLng(new google.maps.Point(tile.x * scale, (tile.y + 1) * scale));
				var ne = projection.fromPointToLatLng(new google.maps.Point((tile.x + 1) * scale, tile.y * scale));

				return birdata.surveyTile(self.filterCriteria(), tile.x, tile.y, tile.z).then(function(points) {
					if(tile.removed) return;
					_.each(points, function(p) {
						var x = Math.floor((p.lon - sw.lng()) * 6 / (ne.lng() - sw.lng()));
						var y = Math.floor((p.lat - sw.lat()) * 6 / (ne.lat() - sw.lat()));

						// Populate ID needed by marker layer
						p.id = [tile.x, tile.y, tile.x, x, y].join(",");
					});
					return points;
				});
			},
			markerForPoint: function(p) {
				var title = 'Survey point';
				if(p.approx) {
					title += ' (approximate location shown for sensitive species)';
				}

				return new google.maps.Marker({
					position: new google.maps.LatLng(p.lat, p.lon),
					title: title,
					icon: mapStyles.surveyIcon(false, p.approx)
				});
			}
		});

		googleMaps.preventAccidentalScroll(map);

		self.filterCriteria.subscribe(function(){
			overlay.refresh();
			surveyPointLayer.refresh();
		});
	}

	function option(text, value) {
		var option = document.createElement('option');
		option.appendChild(document.createTextNode(text));
		option.value = value;
		return option;
	}

	function setupSpeciesSelector() {
		if(setupSpeciesSelector.called) return;
		setupSpeciesSelector.called = true;

		var select = document.createElement('select');

		select.appendChild(option('All Species', 0));
		_.chain(self.species())
			.filter(function(sp) { return sp.sensitivity_id != 1; })
			.sortBy('common_name')
			.each(function(species) {
				select.appendChild(option(species.common_name, species.id));
			});
		select.style.margin = '8px';
		select.style.fontSize = '80%';

		select.addEventListener('change', function() {
			self.speciesFilter(_.findWhere(self.species(), {id: +select.value}));
		});

		self.speciesFilter.subscribe(function(species) {
			var speciesId = species ? species.id : 0;
			if(select.value !== speciesId) {
				select.value = speciesId;
			}
		});

		self.map().controls[google.maps.ControlPosition.TOP_LEFT].push(select);
	}

	ko.computed(function() {
		var map = self.map();
		
		if(map) {
			setupMap(map);
		}

		if(map && self.species()) {
			setupSpeciesSelector();
		}
	});
};


ko.components.register('species-map', {
	viewModel: function(params) {
		return new SpeciesMapViewModel(params);
	},
	template: require('../../templates/species-map.html')
});

export default SpeciesMapViewModel;
